import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import { TMDXFile } from '@/api/apis/files';
import {
  AssignOpoCaseContactsRequestBody,
  CreateOpoDraftCaseRequestBody,
  UnassignOpoCaseContactsRequestBody,
  UpdateOpoCaseDonorDataRequestBody,
  UpdateOpoCaseRequestBody,
  UpdateOpoNrpApplicabilityAndTypeRequestBody,
} from '@/api/apis/opobff/opobff-case.types';
import { opobffCaseQueryKeys } from './opobff-case.keys';
import { invalidateOpoInfiniteCaseListQueries } from './opobff-case.query';

export const useCreateOpoDraftCaseMutation = () => {
  return useMutation({
    mutationFn: (body: CreateOpoDraftCaseRequestBody) => api.opobffCase.createOpoDraftCase(body),
  });
};

export const useUpdateOpoCaseMutation = () => {
  return useMutation({
    mutationFn: (body: UpdateOpoCaseRequestBody & { caseId: string }) => api.opobffCase.updateOpoCase(body),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};

export const useUpdateOpoCaseDonorDataMutation = () => {
  return useMutation({
    mutationFn: (body: UpdateOpoCaseDonorDataRequestBody & { caseId: string }) =>
      api.opobffCase.updateOpoCaseDonorData(body),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};

export const useDeleteOpoDraftCaseMutation = () => {
  return useMutation({
    mutationFn: (caseId: string) => api.opobffCase.deleteOpoDraftCase(caseId),
    onSuccess: () => {
      invalidateOpoInfiniteCaseListQueries();
    },
  });
};

export const useAssignOpoCaseContactsMutation = () => {
  return useMutation({
    mutationFn: (body: AssignOpoCaseContactsRequestBody & { caseId: string }) =>
      api.opobffCase.assignOpoCaseContacts(body),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};

export const useUnassignOpoCaseContactsMutation = () => {
  return useMutation({
    mutationFn: (body: UnassignOpoCaseContactsRequestBody & { caseId: string }) =>
      api.opobffCase.unassignOpoCaseContacts(body),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};

export const useAddOpoCaseFilesMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, files }: { caseId: string; files: TMDXFile[] }) =>
      api.files.caseFileAttach(caseId, { files }),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};

export const useRemoveOpoCaseFileMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, fileId }: { caseId: string; fileId: string }) => api.files.caseFileRemove(caseId, fileId),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};

export const useUpdateOpoCaseNrpApplicabilityAndTypeMutation = () => {
  return useMutation({
    mutationFn: (body: UpdateOpoNrpApplicabilityAndTypeRequestBody & { caseId: string }) =>
      api.opobffCase.updateOpoCaseNrpApplicabilityAndType(body),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: opobffCaseQueryKeys.getItem(caseId) });
    },
  });
};
